import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, SearchableSelect, Table, type TableColumn } from '../ui';
import { formatRelativeDate } from '../../utils/dateUtils';
import {
  listPieces,
  PIECE_STATUS_LABEL,
  pieceStatusTone,
  type ContentPieceListRow,
} from '../../lib/contentPieces';

/**
 * PiecesPanel — Content → Pieces. Every article a content job has handed in,
 * newest first, filterable by client. Click a row for the full-screen piece.
 */

export interface PiecesPanelProps {
  clients: { id: string; name: string }[];
}

type Row = ContentPieceListRow & Record<string, unknown>;

/** Postgres "relation does not exist" — the content_pieces migration has not landed on this database yet. */
function isMissingTable(err: unknown): boolean {
  const e = err as { code?: string; message?: string } | null;
  return e?.code === '42P01' || /content_pieces/.test(e?.message || '') && /does not exist|not found|schema cache/i.test(e?.message || '');
}

export default function PiecesPanel({ clients }: PiecesPanelProps) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await listPieces({ clientId });
        if (!cancelled) {
          setRows(data as Row[]);
          setMissing(false);
        }
      } catch (err) {
        console.error('Error loading content pieces:', err);
        if (!cancelled) {
          if (isMissingTable(err)) setMissing(true);
          else setError(err instanceof Error ? err.message : 'Failed to load pieces');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, tick]);

  const columns: TableColumn<Row>[] = [
    {
      key: 'title',
      label: 'Title',
      // A long nowrap title pushed Updated off the card's right edge even at 1440
      wrap: true,
      render: (_v, row) => (
        <span style={{ color: 'var(--text-body)', fontWeight: 500 }}>{row.title || 'Untitled'}</span>
      ),
    },
    { key: 'client_name', label: 'Client', render: (v) => (v as string | null) || '—' },
    {
      key: 'status',
      label: 'Status',
      render: (_v, row) => (
        <Badge tone={pieceStatusTone(row.status)} dot pulse={row.status === 'images_pending'}>
          {PIECE_STATUS_LABEL[row.status]}
        </Badge>
      ),
    },
    {
      key: 'image_count',
      label: 'Images',
      mono: true,
      render: (_v, row) =>
        row.image_count === 0
          ? '—'
          : row.images_pending > 0
            ? `${row.image_count - row.images_pending}/${row.image_count} · ${row.images_pending} pending`
            : `${row.image_count}/${row.image_count}`,
    },
    { key: 'updated_at', label: 'Updated', mono: true, render: (v) => formatRelativeDate(v as string) },
  ];

  return (
    <Card
      eyebrow="Pieces"
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Narrower select and an icon-only Refresh on phones, so both fit beside the eyebrow */}
          <div className="min-w-[170px] md:min-w-[200px]">
            <SearchableSelect
              placeholder="All clients"
              searchPlaceholder="Search clients..."
              clearLabel="All clients"
              allowClear
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
              value={clientId}
              onChange={setClientId}
            />
          </div>
          <span className="md:hidden">
            <Button size="sm" variant="ghost" icon="refresh-cw" onClick={() => setTick((n) => n + 1)} disabled={loading} aria-label="Refresh" />
          </span>
          <span className="hidden md:inline-flex">
            <Button size="sm" variant="ghost" icon="refresh-cw" onClick={() => setTick((n) => n + 1)} disabled={loading}>
              Refresh
            </Button>
          </span>
        </div>
      }
    >
      {missing ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.55 }}>
          The <code>content_pieces</code> table is not on this database yet. It arrives with the
          <code> 20260905100000_content_pieces</code> migration at approval; until then this list stays empty.
        </p>
      ) : error ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p>
      ) : loading && rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading pieces…</p>
      ) : rows.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-faint)', lineHeight: 1.55 }}>
          No pieces yet. When a writer hands in an article (<code>cockpit content put</code>), it lands here and
          gets a <em>View article</em> button on its job.
        </p>
      ) : (
        <>
          <div className="hidden md:block">
            <Table<Row>
              columns={columns}
              rows={rows}
              onRowClick={(row) => navigate(`/content/pieces/${row.id}`)}
            />
          </div>
          {/* Phones: the five-column table showed only a clipped title; a stacked list shows it all */}
          <div className="md:hidden" style={{ margin: '0 calc(-1 * var(--space-5)) calc(-1 * var(--space-5))' }}>
            {rows.map((row) => (
              <div
                key={row.id}
                className="hover-wash"
                onClick={() => navigate(`/content/pieces/${row.id}`)}
                style={{
                  padding: '12px var(--space-5)',
                  borderTop: '1px solid var(--border-hairline)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ color: 'var(--text-body)', fontWeight: 500, fontSize: 'var(--text-sm)', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                  {row.title || 'Untitled'}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '4px 10px',
                    marginTop: '6px',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-faint)',
                  }}
                >
                  <Badge tone={pieceStatusTone(row.status)} dot pulse={row.status === 'images_pending'}>
                    {PIECE_STATUS_LABEL[row.status]}
                  </Badge>
                  {row.client_name ? <span style={{ color: 'var(--text-muted)' }}>{row.client_name}</span> : null}
                  {row.image_count > 0 ? (
                    <span className="t-num">
                      {row.images_pending > 0
                        ? `${row.images_pending} image${row.images_pending === 1 ? '' : 's'} pending`
                        : `${row.image_count} image${row.image_count === 1 ? '' : 's'}`}
                    </span>
                  ) : null}
                  <span className="t-num">{formatRelativeDate(row.updated_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
